import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { landingForRole } from './utils/roleLanding';
import { ProtectedRoute } from './components/ProtectedRoute';
import LeadPopupForm from './components/LeadPopupForm';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import Footer from './components/Footer';
import ERPLayout from './components/ERPLayout';
import ErrorBoundary from './components/ErrorBoundary';
import AIChatWidget from './components/AIChatWidget';
import CartSidebar from './components/CartSidebar';
import { Loader2 } from 'lucide-react';
import '@/App.css';

// Loading Component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-3" />
      <p className="text-slate-500">Loading...</p>
    </div>
  </div>
);

// ====== CRITICAL PUBLIC PAGES - eager for faster initial load ======
import Home from './pages/Home';
import Login from './pages/Login';

// Public pages - lazy loaded
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const GlassConfigurator3D = lazy(() => import('./pages/GlassConfigurator3D'));
const Industries = lazy(() => import('./pages/Industries'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Resources = lazy(() => import('./pages/Resources'));
const Contact = lazy(() => import('./pages/Contact'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const Cart = lazy(() => import('./pages/Cart'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Wholesale = lazy(() => import('./pages/Wholesale'));
const SharedConfig = lazy(() => import('./pages/SharedConfig'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CMSPage = lazy(() => import('./pages/CMSPage'));

// Customer Portal - lazy loaded
const CustomerPortalEnhanced = lazy(() => import('./pages/CustomerPortalEnhanced'));
const JobWorkPage = lazy(() => import('./pages/JobWorkPage'));
const JobWork3DConfigurator = lazy(() => import('./pages/JobWork3DConfigurator'));

// Legacy Dashboards - lazy loaded
const CustomerDashboard = lazy(() => import('./components/dashboards/CustomerDashboard'));
const DealerDashboard = lazy(() => import('./components/dashboards/DealerDashboard'));
const CustomerAdminDashboard = lazy(() => import('./components/dashboards/CustomerAdminDashboard'));

// ERP Pages - All lazy loaded for better performance
const ERPAdminDashboard = lazy(() => import('./pages/erp/AdminDashboard'));
const CRMDashboard = lazy(() => import('./pages/erp/CRMDashboard'));
const ProductionDashboard = lazy(() => import('./pages/erp/ProductionDashboard'));
const HRDashboard = lazy(() => import('./pages/erp/HRDashboard'));
const OperatorDashboard = lazy(() => import('./pages/erp/OperatorDashboard'));
const InventoryDashboard = lazy(() => import('./pages/erp/InventoryDashboard'));
const PurchaseDashboard = lazy(() => import('./pages/erp/PurchaseDashboard'));
const AccountsDashboard = lazy(() => import('./pages/erp/AccountsDashboard'));
const PayoutsDashboard = lazy(() => import('./pages/erp/PayoutsDashboard'));
const WalletDashboard = lazy(() => import('./pages/erp/WalletDashboard'));
const ExpenseDashboard = lazy(() => import('./pages/erp/ExpenseDashboard'));
const AssetsDashboard = lazy(() => import('./pages/erp/AssetsDashboard'));
const HolidaysDashboard = lazy(() => import('./pages/erp/HolidaysDashboard'));
const SuperAdminOverview = lazy(() => import('./pages/erp/SuperAdminOverview'));
const UserManagementPage = lazy(() => import('./pages/erp/UserManagementPage'));
const AuditActivityPage = lazy(() => import('./pages/erp/AuditActivityPage'));
const PermissionControlPage = lazy(() => import('./pages/erp/PermissionControlPage'));
const CredentialsPage = lazy(() => import('./pages/erp/CredentialsPage'));
const SFADashboard = lazy(() => import('./pages/erp/SFADashboard'));
const OrderManagement = lazy(() => import('./pages/erp/OrderManagement'));
const SettingsDashboard = lazy(() => import('./pages/erp/SettingsDashboard'));
const ForecastDashboard = lazy(() => import('./pages/erp/ForecastDashboard'));
const CMSDashboard = lazy(() => import('./pages/erp/CMSDashboard'));
const BranchDashboard = lazy(() => import('./pages/erp/BranchDashboard'));
const ProductConfigDashboard = lazy(() => import('./pages/erp/ProductConfigDashboard'));
const TransportDashboard = lazy(() => import('./pages/erp/TransportDashboard'));
const DriverDashboard = lazy(() => import('./pages/erp/DriverDashboard'));

const GSTDashboard = lazy(() => import('./pages/erp/GSTDashboard'));
const JobWorkDashboard = lazy(() => import('./pages/erp/JobWorkDashboard'));
const VendorManagement = lazy(() => import('./pages/erp/VendorManagement'));
const LedgerManagement = lazy(() => import('./pages/erp/LedgerManagement'));
const CustomerManagement = lazy(() => import('./pages/erp/CustomerManagement'));
const LeaveManagement = lazy(() => import('./pages/LeaveManagement'));
const AdvancedJobCards = lazy(() => import('./pages/AdvancedJobCards'));
const AdminInquiryDashboard = lazy(() => import('./pages/erp/AdminInquiryDashboard'));
const FloorManagerDashboard = lazy(() => import('./pages/erp/FloorManagerDashboard'));
const GlassWeightSettings = lazy(() => import('./pages/erp/GlassWeightSettings'));
const TallySettings = lazy(() => import('./pages/erp/TallySettings'));
const DiscountApprovalDashboard = lazy(() => import('./pages/erp/DiscountApprovalDashboard'));
const QuotationManagement = lazy(() => import('./pages/erp/QuotationManagement'));

// Sales Management System - Tasks, Targets, Incentives, Performance
const SalesTaskDashboard = lazy(() => import('./pages/erp/SalesTaskDashboard'));
const SalesTargetDashboard = lazy(() => import('./pages/erp/SalesTargetDashboard'));
const IncentiveDashboard = lazy(() => import('./pages/erp/IncentiveDashboard'));
const PerformanceDashboard = lazy(() => import('./pages/erp/PerformanceDashboard'));

// Employee Self-Service Portal
const ESSPortal = lazy(() => import('./pages/ess/ESSPortal'));

// Employee Onboarding
const OnboardingDashboard = lazy(() => import('./pages/erp/OnboardingDashboard'));
const AgreementPage = lazy(() => import('./pages/onboarding/AgreementPage'));

// HR Letters (Warning, Termination)
const HRLettersDashboard = lazy(() => import('./pages/erp/HRLettersDashboard'));

// Lead Integration Dashboard
const LeadsDashboard = lazy(() => import('./pages/erp/LeadsDashboard'));

// Payment Dashboard
const PaymentDashboard = lazy(() => import('./pages/erp/PaymentDashboard'));

// Abandoned Carts Dashboard
const AbandonedCartsDashboard = lazy(() => import('./pages/erp/AbandonedCartsDashboard'));

// Refunds & Cancellations Dashboard
const RefundsDashboard = lazy(() => import('./pages/erp/RefundsDashboard'));

// Sales Invoice Module
const SalesInvoice = lazy(() => import('./pages/erp/SalesInvoice'));

// Purchase Module (PO + Purchase Invoice)
const PurchaseModule = lazy(() => import('./pages/erp/PurchaseModule'));

// Layout wrapper that conditionally shows header/footer
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isERPRoute = location.pathname.startsWith('/erp');
  const isCustomerPortal = location.pathname.startsWith('/portal') || location.pathname.startsWith('/job-work');
  const isESSPortal = location.pathname.startsWith('/ess');
  const isOnboardingAgreement = location.pathname.startsWith('/onboarding/agreement');
  const showChat = !isERPRoute && !isESSPortal && !isOnboardingAgreement; // Show chat on public pages
  
  if (isERPRoute || isCustomerPortal || isESSPortal || isOnboardingAgreement) {
    if (isCustomerPortal) {
      // Customer portal has its own header chrome (logo, cart, notifications,
      // New Order / Job Work). The storefront header is NOT shown here, but the
      // shared cart drawer stays so the portal cart button can open it.
      return (
        <div className="min-h-screen flex flex-col">
          <main className="flex-1">{children}</main>
          <CartSidebar />
        </div>
      );
    }
    if (isESSPortal || isOnboardingAgreement) return children;
    // Wrap ERP routes with ProtectedRoute for role-based access control
    return (
      <ProtectedRoute>
        <ERPLayout>{children}</ERPLayout>
      </ProtectedRoute>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <CartSidebar />
      
      {/* Lead Popup Form - Shows every 45 seconds on public pages */}
      <LeadPopupForm />
      {showChat && <AIChatWidget />}
    </div>
  );
};

// Bare `/erp` entry: send each role to its own default landing page so no role
// (notably sales) is dropped on the admin-only /erp/dashboard.
const ERPLanding = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return <Navigate to={landingForRole(user?.role)} replace />;
};

function AppRoutes() {
  return (
    <AppLayout>
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Website Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/customize" element={<GlassConfigurator3D />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/wholesale" element={<Wholesale />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/login" element={<Login />} />
          <Route path="/track" element={<TrackOrder />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/dashboard" element={<CustomerPortalEnhanced />} />
          <Route path="/dealer-dashboard" element={<DealerDashboard />} />
          <Route path="/admin-dashboard" element={<CustomerAdminDashboard />} />

          {/* Dynamic CMS pages (owner-created custom website pages) */}
          <Route path="/:slug" element={<CMSPage />} />
          
          {/* ERP Routes */}
          <Route path="/erp" element={<ERPLanding />} />
          <Route path="/erp/dashboard" element={<ERPAdminDashboard />} />
          <Route path="/erp/admin" element={<ERPAdminDashboard />} />
          <Route path="/erp/crm" element={<CRMDashboard />} />
          <Route path="/erp/production" element={<ProductionDashboard />} />
          <Route path="/erp/hr" element={<HRDashboard />} />
          <Route path="/erp/operator" element={<OperatorDashboard />} />
          <Route path="/erp/inventory" element={<InventoryDashboard />} />
          {/* Purchase is reachable from Vendor & PO / Store Manager, not the Operations menu */}
          <Route path="/erp/purchase" element={<PurchaseDashboard />} />
          <Route path="/erp/accounts" element={<AccountsDashboard />} />
          <Route path="/erp/payouts" element={<PayoutsDashboard />} />
          {/* WalletDashboard IS the Rewards & Referrals screen (single system) */}
          <Route path="/erp/wallet" element={<WalletDashboard />} />
          <Route path="/erp/expenses" element={<ExpenseDashboard />} />
          <Route path="/erp/assets" element={<AssetsDashboard />} />
          <Route path="/erp/holidays" element={<HolidaysDashboard />} />
          <Route path="/erp/superadmin" element={<SuperAdminOverview />} />
          <Route path="/erp/superadmin/users" element={<UserManagementPage />} />
          {/* Retired routes now folded into /erp/superadmin/audit */}
          <Route path="/erp/superadmin/daily" element={<Navigate to="/erp/superadmin/audit" replace />} />
          <Route path="/erp/superadmin/mis" element={<Navigate to="/erp/superadmin/audit" replace />} />
          <Route path="/erp/activity-log" element={<Navigate to="/erp/superadmin/audit" replace />} />
          <Route path="/erp/superadmin/permissions" element={<PermissionControlPage />} />
          <Route path="/erp/superadmin/credentials" element={<CredentialsPage />} />
          <Route path="/erp/sfa" element={<SFADashboard />} />
          <Route path="/erp/orders" element={<OrderManagement />} />
          <Route path="/erp/settings" element={<SettingsDashboard />} />
          <Route path="/erp/forecast" element={<ForecastDashboard />} />
          <Route path="/erp/cms" element={<CMSDashboard />} />
          <Route path="/erp/branches" element={<BranchDashboard />} />
          <Route path="/erp/product-config" element={<ProductConfigDashboard />} />
          <Route path="/erp/transport" element={<TransportDashboard />} />
          <Route path="/erp/driver-dashboard" element={<DriverDashboard />} />
          <Route path="/erp/rewards" element={<WalletDashboard />} />
          <Route path="/erp/refunds" element={<RefundsDashboard />} />
          <Route path="/erp/gst" element={<GSTDashboard />} />
          <Route path="/erp/job-work" element={<JobWorkDashboard />} />
          <Route path="/erp/vendor-management" element={<VendorManagement />} />
          <Route path="/erp/ledger" element={<LedgerManagement />} />
          <Route path="/erp/customer-master" element={<CustomerManagement />} />
          <Route path="/erp/leave" element={<LeaveManagement />} />
          {/* Old second forecast screen (invoice-based, always "insufficient data")
              now folded into the single accurate Demand Forecast above. */}
          <Route path="/erp/ai-forecast" element={<Navigate to="/erp/forecast" replace />} />
          <Route path="/erp/job-cards" element={<AdvancedJobCards />} />
          <Route path="/erp/inquiries" element={<AdminInquiryDashboard />} />
          <Route path="/erp/floor-manager" element={<FloorManagerDashboard />} />
          <Route path="/erp/glass-weight" element={<GlassWeightSettings />} />
          <Route path="/erp/tally-settings" element={<TallySettings />} />
          {/* PIN CODE MASTER — retired from navigation.
              It only ever existed to validate delivery PIN codes / check
              serviceability and to drive shipping-zone rules. Nothing in
              orders, invoicing, production, dispatch or the warehouse flow
              reads it, so keeping it in the menu implied a dependency that
              does not exist. The backend collection is untouched so the data
              can be revived if a courier/shipping integration needs it. */}
          {/* <Route path="/erp/pincode-settings" element={<PinCodeSettings />} /> */}
          <Route path="/erp/discount-approvals" element={<DiscountApprovalDashboard />} />
          <Route path="/erp/quotations" element={<QuotationManagement />} />
          
          {/* Sales Management System Routes */}
          <Route path="/erp/sales-tasks" element={<SalesTaskDashboard />} />
          <Route path="/erp/sales-targets" element={<SalesTargetDashboard />} />
          <Route path="/erp/incentives" element={<IncentiveDashboard />} />
          <Route path="/erp/performance" element={<PerformanceDashboard />} />

          {/* Employee Self-Service Portal */}
          <Route path="/ess" element={<ESSPortal />} />
          <Route path="/erp/ess" element={<ESSPortal />} />

          {/* Employee Onboarding */}
          <Route path="/erp/onboarding" element={<OnboardingDashboard />} />
          <Route path="/onboarding/agreement/:token" element={<AgreementPage />} />

          {/* HR Letters */}
          <Route path="/erp/hr-letters" element={<HRLettersDashboard />} />

          {/* Audit & Activity (combines Audit Trail + Daily Activity + MIS Report) */}
          <Route path="/erp/superadmin/audit" element={<AuditActivityPage />} />

          {/* Lead Integration Dashboard */}
          <Route path="/erp/leads" element={<LeadsDashboard />} />

          {/* Payment Dashboard */}
          <Route path="/erp/payments" element={<PaymentDashboard />} />

          {/* Abandoned Carts Dashboard */}
          <Route path="/erp/abandoned-carts" element={<AbandonedCartsDashboard />} />

          {/* Activity Report Dashboard */}
          <Route path="/erp/activity-reports" element={<Navigate to="/erp/superadmin/audit" replace />} />

          {/* Sales Invoice Module */}
          <Route path="/erp/sales-invoice" element={<SalesInvoice />} />

          {/* Purchase Module (PO + Purchase Invoice) */}
          <Route path="/erp/purchase-invoice" element={<PurchaseModule />} />

          {/* Customer Portal */}
          <Route path="/portal" element={<CustomerPortalEnhanced />} />
          <Route path="/job-work" element={<JobWork3DConfigurator />} />
          <Route path="/share/:shareId" element={<SharedConfig />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </AppLayout>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
